import * as React from 'react';
import { localization } from '../../localization';
import { ImageSrc, Settings } from '../../CommonTypes';
import { JSXToNode, nonNull, svgToImg, toBlob, toDataUrl } from '../../react_utils';
import { CrispImg, FAButton, Overlayable, Slider } from '../coreui/basic';
import { ZoomableContainer, Point, equal } from '../coreui';
import { getMousePoint, ITool } from '../measure_tools/Base';
import { IImageInfo } from './MeasurementsPane';
import { SaveDialogStyled, SDLoader, SDPreviewStyled, SDSaveBlockStyled, SDUIStyled } from './SaveDialog.styles';
import { IAppProps } from '../../App';
import { imgName } from '../../utils';


const supportedFormatList = ['jpeg', 'png', 'svg'] as const;
export interface ISaveDialogProps {
  getSrcString: IAppProps['utils']['getSrcString'];
  //saveBlob: IAppProps['utils']['saveBlob'];
  saveDialog: IAppProps['utils']['saveDialog'];

  imageInfo: IImageInfo;
  imageSrc: ImageSrc;
  filterString: string;
  settings: Settings;

  measurementRefs: (ITool | null)[];

  renderPortalRef: React.RefObject<SVGSVGElement>;
  overlayScaleVal: number;

  onOverlayScaleValChanged: (newScale: number) => void;

  onFinish: () => void;
}

export interface ISaveDialogState {
  saveImg: boolean;
  saveImgFormat: number;
  saveImgName: string;

  saveCSV: boolean;
  saveCSVName: string;

  cutout?: { p1: Point, p2: Point, changing: boolean };
  saving: boolean;
}

export class SaveDialog extends React.Component<ISaveDialogProps, ISaveDialogState> {
  private imgRef = React.createRef<HTMLImageElement>();
  constructor(props: ISaveDialogProps) {
    super(props);
    const defaultName = imgName(props.imageSrc, props.settings.lang);
    this.state = {
      saveImg: true,
      saveImgFormat: 0,
      saveImgName: defaultName,
      saveCSV: false,
      saveCSVName: defaultName,
      saving: false
    }
  }

  private getCutoutDim = () => {
    if (!this.state.cutout)
      return { x: 0, y: 0, width: this.props.imageInfo.width, height: this.props.imageInfo.height };
    
    const { p1, p2 } = this.state.cutout;
    const x = Math.min(p1.x, p2.x);
    const y = Math.min(p1.y, p2.y);
    const width = Math.max(p1.x, p2.x) - x;
    const height = Math.max(p1.y, p2.y) - y;
    return { x, y, width, height }
  };

  private getPoint = (e: MouseEvent) => {
    const svg = this.props.renderPortalRef.current;
    return svg ? getMousePoint(e, svg) : { x: 0, y: 0 };
  };

  private mouseDown = (e: MouseEvent) => {
    if (e.button === 0) {
      const p = this.getPoint(e);
      this.setState({ cutout: { p1: p, p2: p, changing: true } });
    }
  }

  private mouseUp = (e: MouseEvent) => {
    if (e.button === 0 && this.state.cutout?.changing) {
      const p1 = this.state.cutout.p1
      const p2 = this.getPoint(e);
      if (equal(p1, p2))
        this.setState({ cutout: undefined });
      else
        this.setState({ cutout: { p1, p2, changing: false } });
    }
  }

  private mouseMove = (e: MouseEvent) => {
    const cut = this.state.cutout;
    if (cut?.changing) {
      this.setState({ cutout: { p1: cut.p1, p2: this.getPoint(e), changing: true } });
    }
  };

  private getImgBlob = async () => {
    const format = supportedFormatList[this.state.saveImgFormat];

    const svg = this.props.renderPortalRef.current;
    const img = this.imgRef.current;
    if (svg && img) {
      this.setState({ saving: true });
      const saveSVG = svg.cloneNode(true) as SVGSVGElement;

      const { x, y, width, height } = this.getCutoutDim();
      saveSVG.setAttribute('viewBox', `${x} ${y} ${width} ${height}`);
      saveSVG.setAttribute('width', `${width}px`);
      saveSVG.setAttribute('height', `${height}px`);

      if (format === 'svg') {
        const blob = await toBlob(img.width, img.height, 'image/jpeg', { img });
        const imgUrl = await toDataUrl(blob);
        const image: SVGImageElement = await JSXToNode(<image xlinkHref={imgUrl} width={this.props.imageInfo.width} height={this.props.imageInfo.height} />);
        saveSVG.insertBefore(image, saveSVG.firstChild);

        const svgBlob = new Blob([saveSVG.outerHTML], { type: 'image/svg+xml;charset=utf-8' });
        return svgBlob;
      } else {
        const svgImg = await svgToImg(saveSVG.outerHTML);
        const blob = await toBlob(width, height, `image/${format}`, { img, srcRect: { x, y, width, height } }, { img: svgImg });
        
        return blob;
      }
    } else {
      throw new Error('Failed to get img refs');
    }
  };

  private getCSVBlob = () => {
    this.setState({ saving: true });
    const header = 'Index; Name; Unit; Type; Segment; Sx; Sy; Ex; Ey; Length; Area';
    const lines = this.props.measurementRefs.map(m => m?.getCSVEntries()?.map(line => line.join('; ')) || null).filter(nonNull).flat();
    const text = [header, ...lines].join('\n');

    const blob = new Blob([text], { type: 'octet/stream' });
    return blob;
  };

  private save = async (title: string, name: string, blobGetter: () => Blob | Promise<Blob>) => {
    const handle = await this.props.saveDialog(title, name, this.props.settings.lang);
    if (handle) {
      await handle.save(await Promise.resolve(blobGetter()));
      return true;
    }
    return false;
  };

  private onSaveClick = async () => {
    let savedImage = false;
    let savedCSV = false;
    try {
      if (this.state.saveImg) {
        const format = supportedFormatList[this.state.saveImgFormat];
        const title = `strucTEM Measure: ${localization[this.props.settings.lang].saveDialog.saveImage}`;
        savedImage = await this.save(title, `${this.state.saveImgName}.${format}`, this.getImgBlob);
      }
      if (this.state.saveCSV) {
        const title = `strucTEM Measure: ${localization[this.props.settings.lang].saveDialog.saveCsv}`;
        savedCSV = await this.save(title, `${this.state.saveCSVName}.csv`, this.getCSVBlob);
      }
    } catch (err) {
      if(this.state.saveImg !== savedImage)
        console.log(`Failed to save image: ${err}`);
      if(this.state.saveCSV !== savedCSV)
        console.log(`Failed to save image: ${err}`);
    } finally {
      this.setState({ saving: false });
      if(this.state.saveImg === savedImage && this.state.saveCSV === savedCSV)
        this.props.onFinish();
    }
  };

  componentDidMount = () => {
    document.addEventListener('mousemove', this.mouseMove);
    document.addEventListener('mouseup', this.mouseUp);
    this.forceUpdate();
  };

  componentWillUnmount = () => {
    document.removeEventListener('mousemove', this.mouseMove);
    document.removeEventListener('mouseup', this.mouseUp);
  }

  private preview = () => {
    const { x, y, width, height } = this.getCutoutDim();
    return <SDPreviewStyled>
      <ZoomableContainer>
        <Overlayable base={
          <CrispImg ref={this.imgRef} src={this.props.getSrcString(this.props.imageSrc.src)}
            style={{ filter: this.props.filterString }} />
        }>
          <svg xmlns='http://www.w3.org/2000/svg' xmlnsXlink='http://www.w3.org/1999/xlink'
            ref={this.props.renderPortalRef}
            viewBox={`0 0 ${this.props.imageInfo.width} ${this.props.imageInfo.height}`}
            onMouseDown={e => this.mouseDown(e.nativeEvent)} />
          
          <div id='Cutout' style={{
            left: x - 4,
            top: y - 4,
            width, height
          }} />
        </Overlayable>
      </ZoomableContainer>
    </SDPreviewStyled>;
  };

  private ui = () => {
    const loc = localization[this.props.settings.lang];
    return <SDUIStyled>
      <SDSaveBlockStyled>
        <div className='Selector'>
          <FAButton icon={this.state.saveImg ? 'check-square' : 'square'} onClick={() => this.setState(prev => ({ saveImg: !prev.saveImg }))} />
          <label>{loc.saveDialog.saveImage}</label>
        </div>
        <span>{loc.overlayScale}</span>
        <Slider value={this.props.overlayScaleVal} min={-1} max={2} step={0.025}
          onChange={e => this.props.onOverlayScaleValChanged(parseFloat(e.currentTarget.value))} />
        <span>{loc.saveDialog.filename}</span>
        <input value={this.state.saveImgName} onChange={e => this.setState({ saveImgName: e.currentTarget.value })} />
        <select value={this.state.saveImgFormat} onChange={e => this.setState({ saveImgFormat: parseInt(e.currentTarget.value) })}>
          {supportedFormatList.map((format, i) => (
            <option value={i} key={i}>{`.${format}`}</option>
          ))}
        </select>
      </SDSaveBlockStyled>
      <SDSaveBlockStyled>
        <div className='Selector'>
          <FAButton icon={this.state.saveCSV ? 'check-square' : 'square'} onClick={() => this.setState(prev => ({ saveCSV: !prev.saveCSV }))} />
          <label>{loc.saveDialog.saveCsv}</label>
        </div>

        <span>{loc.saveDialog.filename}</span>
        <input value={this.state.saveCSVName} onChange={e => this.setState({ saveCSVName: e.currentTarget.value })} />
        <span>.csv</span>
      </SDSaveBlockStyled>
          <span>{[loc.imageDisclaimer, ...loc.imageDisclaimerText.split('\n')].map((part, i) => (
            <React.Fragment key={i}>
              {part}
              <br/>
            </React.Fragment>
          ))}</span>
      <div className='Buttons'>
        <button onClick={this.onSaveClick}>{loc.saveDialog.save}</button>
        <button onClick={this.props.onFinish}>{loc.saveDialog.cancel}</button>
      </div>
    </SDUIStyled>
  };

  public render() {
    
    return (
      <SaveDialogStyled onClick={this.state.saving ? undefined : this.props.onFinish}>
        <div onClick={e => e.stopPropagation()}>
          {this.state.saving ? (
            <SDLoader />
          ) : <>
              {this.ui()}
              {this.preview()}
            </>}
        </div>
      </SaveDialogStyled>
    );
  }
}
